<script setup lang="ts">
import { computed } from 'vue';

import { TaskStatus } from '@/api';
import {
  findingThresholdTriggers,
  formatFindingThresholdRule,
  signaturePreparationRequestUid,
  stepStatusTagType,
  type PlaybookActionResult,
  type PlaybookConfiguredAction,
  type PlaybookEvaluation,
  type PlaybookEvaluationResult,
  type PlaybookFindingThresholdTrigger,
} from '@/services/playbooks/playbookRunDisplay';
import SuggestionRedlines from '@/components/playbooks/run-results/SuggestionRedlines.vue';

const props = defineProps<{
  evaluation: PlaybookEvaluation;
  result: PlaybookEvaluationResult;
}>();

type ActionRow = {
  action: PlaybookConfiguredAction;
  result: PlaybookActionResult | null;
};

type TriggerGroup = {
  trigger: PlaybookFindingThresholdTrigger;
  rows: ActionRow[];
};

const resultByAction = computed<Map<string, PlaybookActionResult>>(() => {
  const map = new Map<string, PlaybookActionResult>();
  for (const actionResult of props.result.action_results ?? []) {
    if (actionResult.is_latest) map.set(actionResult.action_uid, actionResult);
  }
  return map;
});

const groups = computed<TriggerGroup[]>(() =>
  findingThresholdTriggers(props.evaluation).map(trigger => ({
    trigger,
    rows: ((trigger.actions ?? []) as PlaybookConfiguredAction[]).map(action => ({
      action,
      result: resultByAction.value.get(action.uid) ?? null,
    })),
  }))
);

function actionTypeLabel(type: PlaybookConfiguredAction['action_type']): string {
  if (type === 'notification') return 'Notification';
  if (type === 'evaluation') return 'Follow-up evaluation';
  if (type === 'signature_preparation') return 'Signature preparation';
  if (type === 'slack_notification') return 'Slack notification';
  if (type === 'jira_notification') return 'Jira notification';
  return 'Suggestion';
}

/** The uid of the signature request this row's action created; null when there is none. */
function signingRequestUid(row: ActionRow): string | null {
  return signaturePreparationRequestUid(row.result);
}

/** The suggestion text of a suggestion result that actually ran; null otherwise. */
function suggestionResultText(row: ActionRow): string | null {
  const { result } = row;
  return result && result.action_type === 'suggestion' ? (result.suggestion ?? null) : null;
}

function actionDetail(row: ActionRow): string | null {
  const { action, result } = row;
  if (action.action_type === 'suggestion') {
    if (result && result.action_type === 'suggestion' && result.suggestion) {
      return result.suggestion;
    }
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
    return action.instruction || null;
  }
  if (action.action_type === 'notification') {
    const recipient =
      (result && result.action_type === 'notification' ? result.recipient_email : null) ??
      action.recipient_email;
    return recipient ? `Recipient: ${recipient}` : null;
  }
  if (action.action_type === 'evaluation') {
    return action.target_evaluation_name
      ? `Triggers: ${action.target_evaluation_name}`
      : 'Triggers a follow-up evaluation';
  }
  if (action.action_type === 'slack_notification') {
    return "Posts to your organisation's Slack channel after the run finishes.";
  }
  if (action.action_type === 'jira_notification') {
    return 'Comments on the source Jira issue after the run finishes.';
  }
  return null;
}
</script>

<template>
  <n-flex v-if="groups.length" vertical :size="16">
    <n-flex v-for="group in groups" :key="group.trigger.uid" vertical :size="4">
      <n-text :depth="2" style="font-size: 13px; font-weight: 600">{{
        formatFindingThresholdRule(group.trigger)
      }}</n-text>
      <n-list v-if="group.rows.length" :show-divider="true">
        <n-list-item v-for="row in group.rows" :key="row.action.uid">
          <n-thing>
            <template #header>
              <span style="font-size: 14px; font-weight: 600">{{ row.action.name }}</span>
            </template>
            <template #header-extra>
              <n-flex :size="6">
                <n-tag size="small" :bordered="false">{{
                  actionTypeLabel(row.action.action_type)
                }}</n-tag>
                <n-tag
                  v-if="row.result"
                  size="small"
                  :type="stepStatusTagType(row.result.status)"
                  :bordered="false"
                >
                  {{ row.result.status }}
                </n-tag>
                <n-tag v-else size="small" :bordered="false">Not run</n-tag>
              </n-flex>
            </template>
            <suggestion-redlines
              v-if="suggestionResultText(row)"
              :suggestion="suggestionResultText(row)"
              style="margin-top: 4px"
            />
            <n-text
              v-else-if="actionDetail(row)"
              tag="p"
              :depth="3"
              style="
                margin: 4px 0 0;
                font-size: 13px;
                line-height: 1.5;
                white-space: pre-wrap;
                word-break: break-word;
              "
            >
              {{ actionDetail(row) }}
            </n-text>
            <n-text
              v-if="row.result?.status === TaskStatus.FAILED && row.result.error"
              type="error"
              tag="p"
              style="
                margin: 4px 0 0;
                font-size: 13px;
                line-height: 1.5;
                white-space: pre-wrap;
                word-break: break-word;
              "
            >
              {{ row.result.error }}
            </n-text>
            <RouterLink
              v-if="signingRequestUid(row)"
              :to="{
                name: 'signing-request-detail',
                params: { signingRequestUid: signingRequestUid(row) },
              }"
              style="display: inline-block; margin-top: 6px; font-size: 13px"
            >
              Open signature request
            </RouterLink>
          </n-thing>
        </n-list-item>
      </n-list>
      <n-empty v-else size="small" description="No actions on this trigger." />
    </n-flex>
  </n-flex>
  <n-empty v-else description="No finding triggers configured." />
</template>
